import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges } from '@angular/core';

interface DownloadValue {
  id?: number;
  name: string;
  pdf: string;
  price?: number;
  selected?: boolean;
}

interface Download {
  id: number;
  label: string;
  values: DownloadValue[];
}

@Component({
  selector: 'app-download-selection-modal',
  standalone: false,
  templateUrl: './download-selection-modal.component.html',
  styleUrl: './download-selection-modal.component.scss',
})
export class DownloadSelectionModalComponent implements OnInit, OnChanges {
  @Input() showModal = false;
  @Input() downloads: Download[] = [];
  @Input() productName = '';

  @Output() closeEmitter = new EventEmitter<void>();
  @Output() downloadSelectionConfirmed = new EventEmitter<DownloadValue[]>();

  selectedDownloads: DownloadValue[] = [];
  totalPrice = 0;

  // Make parseFloat available in template
  parseFloat = parseFloat;

  ngOnInit() {
    this.initializeDownloads();
    this.calculateTotal();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['downloads'] || changes['showModal']) {
      this.initializeDownloads();
      this.calculateTotal();
    }
  }

  initializeDownloads() {
    // Reset selected downloads array
    this.selectedDownloads = [];
    
    // Initialize selected property for all download values
    if (this.downloads) {
      this.downloads.forEach((download, index) => {
        if (download.values) {
          download.values.forEach((value, valueIndex) => {
            value.selected = false;
          });
        }
      });
    }
  }

  toggleDownload(download: Download, value: DownloadValue) {
    value.selected = !value.selected;
    
    if (value.selected) {
      // Add to selected downloads if not already there
      if (!this.selectedDownloads.find(d => d === value)) {
        this.selectedDownloads.push(value);
      }
    } else {
      // Remove from selected downloads
      const index = this.selectedDownloads.findIndex(d => d === value);
      if (index > -1) {
        this.selectedDownloads.splice(index, 1);
      }
    }
    
    this.calculateTotal();
  }

  calculateTotal() {
    this.totalPrice = this.selectedDownloads.reduce((total, download) => {
      const price = parseFloat(download.price?.toString() || '0');
      return total + (isNaN(price) ? 0 : price);
    }, 0);
  }

  getTotalPrice(): number {
    return this.selectedDownloads.reduce((total, download) => {
      const price = parseFloat(download.price?.toString() || '0');
      return total + (isNaN(price) ? 0 : price);
    }, 0);
  }

  confirmSelection() {
    this.downloadSelectionConfirmed.emit(this.selectedDownloads);
    this.closeModal();
  }

  confirmSelectionWithoutDownloads() {
    this.downloadSelectionConfirmed.emit([]);
    this.closeModal();
  }

  closeModal() {
    this.closeEmitter.emit();
  }

  hasDownloadsWithPrice(): boolean {
    return this.downloads?.some(download => 
      download.values?.some(value => {
        const price = parseFloat(value.price?.toString() || '0');
        return !isNaN(price) && price > 0;
      })
    ) || false;
  }

  // Helper method to check if a download category has any paid downloads
  hasPaidDownloadsInCategory(download: Download): boolean {
    return download.values?.some(value => {
      const price = parseFloat(value.price?.toString() || '0');
      return !isNaN(price) && price > 0;
    }) || false;
  }

  // Helper method to check if a specific download is paid
  isPaidDownload(value: DownloadValue): boolean {
    const price = parseFloat(value.price?.toString() || '0');
    return !isNaN(price) && price > 0;
  }

  // Helper method to check if a specific download is free
  isFreeDownload(value: DownloadValue): boolean {
    const price = parseFloat(value.price?.toString() || '0');
    return isNaN(price) || price === 0;
  }
}
